import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import MuiAvatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import IconButton from '@mui/material/IconButton';
import AvatarGroup from '@mui/material/AvatarGroup';
import HubIcon from '@mui/icons-material/Hub';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { css } from '@emotion/react';

import { getFragmentData } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';

import { iconToMuiIcon } from '../../utils/icon-to-mui-icon.util';
import { useColorToMuiColor } from '../../hooks/useColorToMuiColor.hook';
import {
  PATH_BUSINESS_EVENT_DEFAULT,
  PATH_FOLDER_DEFAULT,
  PATH_MISSION_DEFAULT,
} from '../../app/constants/paths.constant';
import { formatDateTime } from '../../utils/format-date-time.util';

import { Avatar } from '../Avatar';
import { EllipsisTooltip } from '../EllipsisTooltip';
import { useGoogleApi } from '../Map/hooks/google/useGoogleApi.hook';
import { AddressCardCmp_Address_Fragment } from '../Addresses/Card';
import { ExpandMoreCard } from '../ExpandMoreCard';

import { DEFAULT_FOLDER_BUSINESS_ENTITY_PLACEHOLDER_HEIGHT } from './constants/card.constant';
import { FolderBusinessEntityListCmp_ThirdParty_Fragment } from './fragments/third-party.fragment';
import { ActorAvatar } from './ActorAvatar';
import { ExternalActorsCarousel } from './ExternalActorsCarousel';
import { ClampedDescription } from './ClampedDescription';
import { useActorCategoriesLabel } from './hooks/useActorCategoriesLabel.hook';
import { emittersFirst } from './utils/emitters-first.util';
import { MissionSummary } from './MissionSummary';
import { SubMissionsTree } from './SubMissionsTree';
import type { FolderBusinessEntityListCmp_FolderBusinessEntity } from './fragments/folder-business-entity.fragment';
import type { FolderBusinessEntityState } from './types/folder-business-entity-state.type';
import { buildMissionPath } from '../../app/utils/mission-space.util';
import { hasSubMissions } from '../../app/utils/mission-kind-profile.util';

export interface Props {
  readonly item: FolderBusinessEntityListCmp_FolderBusinessEntity &
    Readonly<{
      state: FolderBusinessEntityState;
    }>;
}

export const FolderBusinessEntityCard = ({
  item: folderBusinessEntity,
}: Readonly<Props>) => {
  const { colorToMuiColor } = useColorToMuiColor();

  const address = getFragmentData(
    AddressCardCmp_Address_Fragment,
    folderBusinessEntity.consolidation.address,
  );

  const { itineraryUrl, navigate, staticMapPreviewUrl } = useGoogleApi({
    address,
  });

  const startNavigationLabel = 'Lancer la Navigation';
  const isConfidentialLabel = 'Confidentiel';
  const isCollaborationLabel = 'Collaboration';

  const getCategoriesLabel = useActorCategoriesLabel();

  // The expand button unfolds the description when it overflows, and the sub-missions of a
  // collaboration.
  const [expanded, setExpanded] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);
  const subMissions =
    folderBusinessEntity.__typename === 'Mission' &&
    hasSubMissions(folderBusinessEntity.kind)
      ? folderBusinessEntity.subMissions
      : undefined;
  const isUnfoldable = !!subMissions || isDescriptionOverflowing;

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  let textualContent;
  let consultPath: string;

  switch (folderBusinessEntity.__typename) {
    case 'Folder':
      consultPath = buildPath(PATH_FOLDER_DEFAULT, {
        folderId: folderBusinessEntity._id,
      });
      textualContent = (
        <>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {folderBusinessEntity.consolidation.title}
          </Typography>
          <EllipsisTooltip title={folderBusinessEntity.code}>
            <Typography noWrap variant="body2">
              {folderBusinessEntity.code}
            </Typography>
          </EllipsisTooltip>
        </>
      );
      break;
    case 'Mission': {
      consultPath = buildMissionPath(PATH_MISSION_DEFAULT, {
        folderId: folderBusinessEntity.folderId,
        missionId: folderBusinessEntity._id,
        kind: folderBusinessEntity.kind,
        compositeMissionId: folderBusinessEntity.compositeMissionId,
      });
      textualContent = <MissionSummary mission={folderBusinessEntity} />;
      break;
    }
    case 'BusinessEvent':
      consultPath = buildPath(PATH_BUSINESS_EVENT_DEFAULT, {
        id: folderBusinessEntity._id,
      });
      textualContent = (
        <Typography
          component="div"
          gutterBottom
          sx={{ lineHeight: 1.2 }}
          variant="h6"
        >
          {folderBusinessEntity.consolidation.title}
        </Typography>
      );
      break;
    default:
      throw new Error('Unknown folder business entity kind');
  }

  return (
    <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        action={
          <Tooltip title={startNavigationLabel}>
            <span>
              <IconButton
                aria-label={startNavigationLabel}
                disabled={!itineraryUrl}
                onClick={navigate}
              >
                <AssistantDirectionIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
        avatar={
          <>
            <Avatar
              bgColor={colorToMuiColor(folderBusinessEntity.state.chip.color)}
              icon={iconToMuiIcon(folderBusinessEntity.state.chip.icon)}
              label={`État : ${folderBusinessEntity.state.label}`}
            />
            <AvatarGroup spacing={20} sx={{ ml: 1 }}>
              {emittersFirst(
                folderBusinessEntity.consolidation.internalActors,
                folderBusinessEntity.consolidation.quotationEmitterActors,
              ).map((internalActor) => {
                const thirdParty = getFragmentData(
                  FolderBusinessEntityListCmp_ThirdParty_Fragment,
                  internalActor.thirdParty,
                );

                return (
                  <ActorAvatar
                    categoriesLabel={getCategoriesLabel(
                      internalActor.categoryIds,
                    )}
                    key={thirdParty._id}
                    thirdParty={thirdParty}
                  />
                );
              })}
            </AvatarGroup>
            {folderBusinessEntity.__typename === 'Mission' &&
              hasSubMissions(folderBusinessEntity.kind) && (
                <Tooltip title={isCollaborationLabel}>
                  <MuiAvatar aria-label={isCollaborationLabel} sx={{ ml: 1 }}>
                    <HubIcon />
                  </MuiAvatar>
                </Tooltip>
              )}
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!folderBusinessEntity.isConfidential && (
              <Tooltip title={isConfidentialLabel}>
                <MuiAvatar aria-label={isConfidentialLabel} sx={{ ml: 1 }}>
                  <LockPersonIcon />
                </MuiAvatar>
              </Tooltip>
            )}
          </>
        }
        css={css`
          .MuiCardHeader-action {
            display: contents;
          }
          /* The avatars of an AvatarGroup get a 2px border: centred, they stay
             aligned with the state and badge avatars. */
          .MuiCardHeader-avatar {
            align-items: center;
          }
        `}
      />
      <CardMedia
        alt="Aperçu de la carte"
        component="img"
        height={DEFAULT_FOLDER_BUSINESS_ENTITY_PLACEHOLDER_HEIGHT}
        image={staticMapPreviewUrl}
      />
      <CardContent>
        <ExternalActorsCarousel
          actors={folderBusinessEntity.consolidation.externalActors}
        />
        {textualContent}
        <Typography variant="body2">
          Ouvert{' '}
          {formatDateTime(folderBusinessEntity.businessCreationDate, {
            datePrefix: 'le ',
          })}
        </Typography>
        <ClampedDescription
          description={folderBusinessEntity.consolidation.description}
          isUnfolded={expanded}
          onOverflowChange={setIsDescriptionOverflowing}
        />
      </CardContent>
      <CardActions disableSpacing>
        <Button component={Link} to={consultPath} variant="outlined">
          Consulter
        </Button>
        {isUnfoldable ? (
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={handleExpandClick}
          />
        ) : null}
      </CardActions>
      {!!subMissions && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <SubMissionsTree subMissions={subMissions} />
          </CardContent>
        </Collapse>
      )}
    </Card>
  );
};
